import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Text, Card, Box, Flex, ThemeIcon, Button, Modal } from "@mantine/core";
import { IconBell, IconCircleFilled } from "@tabler/icons-react";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";

import { formatDate, getAlertEntityInfoFromContext } from "./helpers";

import { AlertDetail } from "./";

export default function AlertList({
  alerts,
  onAlertClick,
  showDate = false,
  maxHeight = "unset",
  activeAlertId = null,
  showRead = true,
  expandable = false,
  preview = false,
  isWidget = false,
  highlightedAlertId = null,
  onDismiss = () => {},
}) {
  const listRef = useRef(null);
  const [presentedAlertIds, setPresentedAlertIds] = useState(new Set());
  const scrollTimeoutRef = useRef(null);
  const presentedAlertIdsRef = useRef(new Set());
  const sentAlertIdsRef = useRef(new Set()); // Track which alerts have already been sent
  const scrollHandlerRef = useRef(null);
  const adminInfo = useSelector((state) => state.admin);
  const managerInfo = useSelector((state) => state.manager);

  // Track which alerts have been presented to the user
  useEffect(() => {
    if (!isWidget || !listRef.current) {
      return;
    }

    // Find the scrollable container - try multiple selectors
    let container = listRef.current.querySelector('[style*="overflowY"]');
    if (!container) {
      container = listRef.current.querySelector('[style*="overflow"]');
    }
    if (!container) {
      container = listRef.current;
    }

    // Create the scroll handler function
    const handleScroll = () => {
      // Clear existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // Get all alert items
      const alertItems = container.querySelectorAll("[data-alert-id]");

      const containerRect = container.getBoundingClientRect();
      const newPresentedIds = new Set();

      alertItems.forEach((item) => {
        const itemRect = item.getBoundingClientRect();
        const alertId = item.dataset.alertId;

        // Check if the alert item is visible in the container
        if (
          itemRect.top < containerRect.bottom &&
          itemRect.bottom > containerRect.top &&
          alertId !== undefined
        ) {
          newPresentedIds.add(alertId);
        }
      });

      // Update presented alerts using ref to avoid dependency issues
      presentedAlertIdsRef.current = new Set([
        ...presentedAlertIdsRef.current,
        ...newPresentedIds,
      ]);
      setPresentedAlertIds(presentedAlertIdsRef.current);

      // Set timeout to send viewed alerts when scrolling stops
      scrollTimeoutRef.current = setTimeout(() => {
        // Only send alerts that haven't been sent before
        const unsentAlertIds = Array.from(presentedAlertIdsRef.current).filter(
          (id) => !sentAlertIdsRef.current.has(id)
        );

        if (unsentAlertIds.length > 0) {
          axios
            .post("/notifications/widget-mark-presented/", {
              notification_ids: unsentAlertIds,
              organization_manager_id: managerInfo?.organization_manager_id,
              location_manager_id: managerInfo?.location_manager_id,
              admin_id: adminInfo?.admin_id,
            })
            .then(() => {
              // Mark these alerts as sent
              unsentAlertIds.forEach((id) => sentAlertIdsRef.current.add(id));
            })
            .catch((error) => {
              unsentAlertIds.forEach((id) => sentAlertIdsRef.current.add(id));
            });
        }
      }, 1000); // Wait 1 second after scrolling stops
    };

    // Store the handler reference
    scrollHandlerRef.current = handleScroll;

    // Add event listener to both the container and the window
    container.addEventListener("scroll", handleScroll);
    window.addEventListener("scroll", handleScroll);

    // Initial check for visible alerts
    handleScroll();

    return () => {
      if (scrollHandlerRef.current) {
        container.removeEventListener("scroll", scrollHandlerRef.current);
        window.removeEventListener("scroll", scrollHandlerRef.current);
      }
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [isWidget, alerts]); // Removed presentedAlertIds dependency

  if (preview && alerts.length > 0) {
    return (
      <Box style={{ position: "relative" }} mih={20}>
        <Card p={0} style={{ zIndex: 10 }}>
          <AlertItem
            alert={alerts[0]}
            onAlertClick={onAlertClick}
            activeAlertId={activeAlertId}
            showDate={showDate}
            expandable={expandable}
            highlightedAlertId={highlightedAlertId}
          />
        </Card>
        {alerts.length > 1 && (
          <Box style={{ position: "relative", height: 20 }}>
            {alerts.slice(1, 4).map((alert, index) => (
              <Card
                style={{
                  position: "absolute",
                  top: -60 + index * 6,
                  left: "50%",
                  transform: `translate(-50%, 0) scale(${0.95 - index * 0.03})`,
                  width: `${100 - index * 1}%`,
                  zIndex: 3 - index,
                }}
                p={0}
                key={alert.id}
                mih={68}
              ></Card>
            ))}
          </Box>
        )}
      </Box>
    );
  }

  return (
    <div ref={listRef}>
      {alerts.length > 0 ? (
        <Box style={{ maxHeight: maxHeight, overflowY: "auto" }}>
          {alerts.map((alert) => {
            return (
              <AlertItem
                key={alert.id}
                alert={alert}
                onAlertClick={onAlertClick}
                activeAlertId={activeAlertId}
                showDate={showDate}
                expandable={expandable}
                isWidget={isWidget}
                highlightedAlertId={highlightedAlertId}
                onDismiss={onDismiss}
              />
            );
          })}
        </Box>
      ) : (
        <Box
          ta="center"
          py="md"
          style={{
            height: 300,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <IconBell color="var(--mantine-color-blue-5)" size={64} />
          <Text size="sm" c="dimmed" mt="md">
            You have no notifications at the moment
          </Text>
        </Box>
      )}
    </div>
  );
}

const AlertItem = ({
  alert,
  onAlertClick,
  activeAlertId,
  showDate,
  expandable,
  isWidget,
  highlightedAlertId,
  onDismiss,
}) => {
  const { icon, color } = getAlertEntityInfoFromContext(alert);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (expandable) {
      setIsOpen(true);
    }
  }, []);

  // useEffect(() => {
  //   if (expandable && activeAlertId === alert.id) {
  //     setIsOpen(true);
  //   }
  // }, [activeAlertId]);

  const isHighlighted =
    highlightedAlertId === alert.id ||
    highlightedAlertId === String(alert.id) ||
    String(highlightedAlertId) === alert.id;

  return (
    <Box
      data-alert-id={alert.id}
      key={alert.id}
      p="xs"
      style={{
        borderBottom: "1px solid #eee",
        cursor: "pointer",
        zIndex: 2,
        backgroundColor: isHighlighted
          ? "var(--mantine-color-yellow-1)"
          : activeAlertId === alert.id
          ? "transparent"
          : "transparent",
        transition: isHighlighted ? "background-color 0.3s ease" : "none",
      }}
      onMouseEnter={(e) => {
        if (activeAlertId !== alert.id && !isHighlighted) {
          // e.currentTarget.style.backgroundColor = `var(--mantine-color-${alertColor}-0)`;
          e.currentTarget.style.backgroundColor = "#f8f9fa";
        }
      }}
      onMouseLeave={(e) => {
        if (activeAlertId !== alert.id && !isHighlighted) {
          e.currentTarget.style.backgroundColor = "transparent";
        } else if (isHighlighted) {
          e.currentTarget.style.backgroundColor =
            "var(--mantine-color-yellow-1)";
        }
      }}
      onClick={() => {
        if (!expandable) {
          onAlertClick(alert);
        }
      }}
    >
      <Flex align="flex-start" gap="md">
        <ThemeIcon color={color} size="sm" radius="xl" variant="light">
          {icon}
        </ThemeIcon>
        <div style={{ flexGrow: 1 }}>
          {isOpen ? (
            <Box>
              <AlertDetail
                alert={alert}
                // alertInfo={alert}
                // alertId={alert.id}
                onClose={() => setIsOpen(false)}
                onDismiss={() => onDismiss(alert.id)}
              />
            </Box>
          ) : (
            <div>
              <Text fw={600} size="sm" lineClamp={1}>
                {alert.title}
              </Text>
              <Text size="sm" mt={2} c="gray.8" lineClamp={1}>
                {alert.message_text}
              </Text>
            </div>
          )}
        </div>
        {showDate && (
          <div style={{ width: 100 }}>
            <Text size="xs" c="gray.6" align="right">
              {formatDate(alert.created_at)}
            </Text>
          </div>
        )}
      </Flex>
    </Box>
  );
};

export const DismissButton = ({ reqData, onSuccess, count = null }) => {
  const [open, setOpen] = useState(false);

  const onDismissClick = () => {
    axios
      .post("/notifications/dismiss-notifications/", reqData)
      .then(() => {
        setOpen(false);
        onSuccess();
      })
      .catch((err) => {
        toast.error(err);
      });
  };
  return (
    <>
      <Button
        onClick={() => {
          if (!count) {
            onDismissClick();
          } else {
            setOpen(true);
          }
        }}
        size="xs"
        variant="outline"
        color="gray"
      >
        {count ? `Dismiss all` : "Dismiss"}
      </Button>
      <Modal
        opened={open}
        onClose={() => setOpen(false)}
        title="Dismiss all notifications"
      >
        <Text fw={600}>
          Are you sure you want to dismiss all ({count}) notifications? This
          action cannot be undone.
        </Text>
        <Button size="md" fullWidth mt="md" onClick={onDismissClick}>
          Dismiss notifications
        </Button>
      </Modal>
    </>
  );
};
